import {
  CalendarEvent,
  Command,
  CustomEvent,
  HolidayEvent,
  Shift,
  Task,
  TimeOffEvent,
  UserRole,
} from '@nextforce/api'
import React, { memo, useCallback, useMemo } from 'react'
import { StyleSheet, View } from 'react-native'
import CustomEventView from './custom_events'
import { HolidayEventView } from './holiday'
import { OpenShiftsGroupEventView, ShiftsGroupEventView } from './shifts_group'
import { TaskEventSummaryView } from './task'
import { TimeOffEventView } from './time_off_requests'

type EventViewProps = {
  role: UserRole
  event: CalendarEvent
  expanded: boolean
  onShiftDetails(shift: Shift): void
  onPressTimeOffSummary(storeId: string): void
  onHelperPressPendingTimeOff(timeOffEvent: TimeOffEvent): void
  onEventExpand(eventId: string): void
  performOpenShiftAction(command: Command): Promise<Shift | undefined>
  onCommandButtonPress(command: Command): Promise<void>
  onEditCustomEvent: (customEvent: CustomEvent) => void
  onEditHolidayEvent: (holidayEvent: HolidayEvent) => void
  showSummaryTiles: boolean
  onPressTask: (task: Task) => void
}

// Memoized child components
const MemoizedCustomEventView = memo(CustomEventView)
const MemoizedHolidayEventView = memo(HolidayEventView)
const MemoizedOpenShiftsGroupEventView = memo(OpenShiftsGroupEventView)
const MemoizedShiftsGroupEventView = memo(ShiftsGroupEventView)
const MemoizedTimeOffEventView = memo(TimeOffEventView)
const MemoizedTaskEventSummaryView = memo(TaskEventSummaryView)

export const EventView = memo(function EventView({
  role,
  event,
  expanded,
  onShiftDetails,
  onEventExpand,
  performOpenShiftAction,
  onPressTimeOffSummary,
  onHelperPressPendingTimeOff,
  onCommandButtonPress,
  onEditCustomEvent,
  onEditHolidayEvent,
  showSummaryTiles,
  onPressTask,
}: EventViewProps) {
  // Memoize callbacks to prevent unnecessary re-renders
  const handleEventExpand = useCallback(() => {
    onEventExpand(event.id)
  }, [event.id, onEventExpand])

  const handleShiftDetails = useCallback(
    (shift: Shift) => {
      onShiftDetails(shift)
    },
    [onShiftDetails]
  )

  const handleCommandButtonPress = useCallback(
    (command: Command) => {
      return onCommandButtonPress(command)
    },
    [onCommandButtonPress]
  )

  // Memoize child view to prevent unnecessary re-renders
  const childView = useMemo(() => {
    switch (event.type) {
      case 'shifts':
        return (
          <MemoizedShiftsGroupEventView
            role={role}
            event={event}
            expanded={expanded}
            onShiftDetails={handleShiftDetails}
            onEventExpand={handleEventExpand}
            showSummaryTiles={showSummaryTiles}
            onCommandButtonPress={handleCommandButtonPress}
          />
        )
      case 'open_shifts':
        return (
          <MemoizedOpenShiftsGroupEventView
            event={event}
            performOpenShiftAction={performOpenShiftAction}
          />
        )
      case 'holiday':
        return (
          <MemoizedHolidayEventView
            event={event}
            onEditHolidayEvent={onEditHolidayEvent}
          />
        )
      case 'time_off':
        return (
          <MemoizedTimeOffEventView
            event={event}
            expanded={expanded}
            showSummaryTiles={showSummaryTiles}
            onStorePressTimeOff={onPressTimeOffSummary}
            onEventExpand={handleEventExpand}
            onHelperPressPendingTimeOff={onHelperPressPendingTimeOff}
          />
        )
      case 'custom_events':
        return (
          <MemoizedCustomEventView
            event={event}
            onEditCustomEvent={onEditCustomEvent}
          />
        )
      case 'tasks':
        return (
          <MemoizedTaskEventSummaryView
            event={event}
            onPressTask={onPressTask}
          />
        )
    }
  }, [
    event,
    role,
    expanded,
    handleShiftDetails,
    handleEventExpand,
    showSummaryTiles,
    handleCommandButtonPress,
    performOpenShiftAction,
    onEditHolidayEvent,
    onPressTimeOffSummary,
    onHelperPressPendingTimeOff,
    onEditCustomEvent,
    onPressTask,
  ])

  return <View style={styles.container}>{childView}</View>
})

export const EVENT_BOTTOM_MARGIN = 0

const styles = StyleSheet.create({
  container: {
    marginHorizontal: 16,
    marginBottom: EVENT_BOTTOM_MARGIN,
  },
})
